import React, { useEffect, useState } from "react";
import { getSeqId } from 'df-helpers/dist/get-seq-id';
import { ReactModel } from "../../react-model/react-model";
import { useOnModelChange } from "../hooks/use-on-model-change";
import { useValidator } from "../hooks/use-validator";
import { LabelInputLinker } from "./label-input-linker";
import "./h-input.css"
import { useStaticMemo } from "../hooks/use-static-memo";

/** The old onChange() is still called after is model is updated */
export type HInputProps<T> = React.InputHTMLAttributes<any> & {
	model: ReactModel<T>;
	field?: keyof NonNullable<T>;
	// field_label?: keyof T;
	// label?: string;
	no_popover?: boolean;
	/** Trim start and end of text value */
	trim?: boolean;
	/** Set the component properties when then model changes */
	onModelChange?: (state: T) => void | React.InputHTMLAttributes<any>;
	/**
	 * Set the model when then input element changes.
	 *
	 * It behaves the same way like setState((state)=>{...})
	 *
	 * Note: the `field` is suggested to be `undefined`, otherwise the field will be written before calling this function.
	 */
	onViewChange?: (state: T, value: any) => void | T;

	/**
	 * When the entered value can be corrected by the model "setter" method:
	 * First display the user entered value;
	 * Then after this milli-seconds of delay, display the model value.
	 *
	 * Class correction_delay is added during the delay
	 */
	correction_dly_ms?: number;

	/** The word "ref" is reserved. We have to use ref2 */
	ref2?: { current: HTMLInputElement | null };
};

export function HInput<T>({
	model,
	field,
	// field_label,
	// label,
	ref2,
	no_popover,
	trim,
	correction_dly_ms,
	onModelChange,
	onViewChange,
	...props
}: HInputProps<T>) {
	if (!ref2) ref2 = { current: null as any };

	const is_checkbox = props.type === "checkbox";
	//load value from model to props.value or props.checked
	if (field !== undefined) {
		var { state, setState: setValue } = model.useModel(field as any);
		if (is_checkbox) {
			props.checked = !!state;
			if (!props.style) props.style = {};
			// props.style.cursor = 'pointer';
		} else {
			const value = state;
			if (value === 0) props.value = 0;
			else props.value = value || ("" as any);
		}
		//add validator
		props = useValidator<T>(model, field as any, ref2, props, no_popover);
	} else {
		props = { ...props }; //unfreeze the object for useOnModelChange
	}

	//create unique id, and then prepare to link to <label>
	{
		const [id] = useState(() => getSeqId("_seq_input_")); //cannot be called under condition
		if (!props.id) {
			props.id = id;
		}
		useEffect(() => {
			LabelInputLinker.addInput(model, field as string, id);
		}, []);
	}

	const old_onchange = props.onChange;

	useOnModelChange(model, onModelChange, props);
	// if(field  === undefined && ! onModelChange){
	// 	console.error('(HI76)');
	// }

	if (correction_dly_ms) {
		var [refresh, setRefresh] = useState(0);
		var correction_context = useStaticMemo(() => {
			let timer_handler = 0 as any;
			let entered_value = undefined as any;

			return {
				enterValue: (v: any, refresh_count:number) => {
					//setRefresh is necessary because the model may not change, and no refresh will occur
					setRefresh(refresh_count+1);
					entered_value = v;
				},
				updateDisplay: (props: React.InputHTMLAttributes<any>, refresh_count: number) => {
					//clear timer
					{
						timer_handler && clearTimeout(timer_handler);
						timer_handler = 0;
					}

					//if model value and the "entered_value" is different
					if (entered_value !== undefined && entered_value !== props.value) {
						//add to class name
						props.className = (props.className || "") + " correction_delay";
						//correct by the model value after some delay
						{
							timer_handler = setTimeout(() => {
								entered_value = undefined;
								setRefresh(refresh_count + 1);
							}, correction_dly_ms);
						}
						//show entered value, and will correct in the timer handler
						props.value = entered_value;
					}else{
						//if missed, then: considered as different when the model changes
						entered_value = undefined;
					}
				},
			};
		});
		correction_context.updateDisplay(props, refresh);
	}

	return (
		<input
			{...props}
			// data-model-id={model.model_id.toString()}
			// data-bs-toggle="popover"
			// data-bs-title="Popover title"
			// data-bs-content="32"
			ref={ref2}
			onChange={e => {
				let v = is_checkbox ? e.currentTarget.checked : (e.currentTarget.value as any);
				if (props.type === "number") {
					v = e.currentTarget.valueAsNumber;
				}
				if (typeof v === "string" && trim) {
					v = v.trim();
				}

				if (correction_dly_ms) {
					correction_context.enterValue(v, refresh);
				}

				if (field !== undefined) {
					setValue(v);
					//enable validator if exists. In this way, no error displayed before the entry starts
					model.validator(false)?.enable(field as any, true);
				}
				if (onViewChange) {
					model.setState(state => {
						return onViewChange(state, v);
					});
				}

				// setState({ ...state, [field]: v });
				// model.setState(old_state => {
				// 	if (field) {
				// 		old_state[field] = v;
				// 	}
				// 	const new_state = onViewChange?.(v, old_state);
				// 	if(new_state !== undefined)
				// 		return new_state;
				// });
				old_onchange?.(e);
			}}
		/>
	);
}
